import React, { useEffect, useState } from "react";
import Navigation from "../../components/Navigation/Navigation";
import "./billing.css";
import visaLogo from "../../assets/cardLogos/visa.jpeg";
import mastercardLogo from "../../assets/cardLogos/mastercard.jpeg";
import amexLogo from "../../assets/cardLogos/amex.png";
import unknownCardLogo from "../../assets/cardLogos/unknown_card_logo.png";
import { request } from "../../utils/axios-utils";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import Modal from "../../components/Modal/Modal";
import DeleteModal from "../../components/DeleteModal/DeleteModal";

const getCardBrandLogo = (brand) => {
  const brandLogoMap = {
    visa: visaLogo,
    mastercard: mastercardLogo,
    amex: amexLogo,
  };

  return brandLogoMap[brand?.toLowerCase()] || unknownCardLogo;
};

const capitalizeFirstLetter = (str) => {
  if (!str) return ""; // Handle empty strings or null values
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);

  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const Billing = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [billingData, setBillingData] = useState({});
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [activeModal, setActiveModal] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    type: "",
    id: "",
  });
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [resumeData, setResumeData] = useState({
    subscriptionPlan: "yearly",
    paymentMethodId: null,
  });
  const [password, setPassword] = useState("");

  const changePaymentMethod = async () => {
    if (paymentMethodId === "") {
      setError("Please select your payment method.");
      return;
    } else if (paymentMethodId === billingData.paymentMethod.id) {
      setError("This payment method is already in use. ");
      return;
    }

    try {
      setLoading("changePaymentMethod");

      const response = await request({
        url: "/member/change-payment-method",
        method: "POST",
        data: { newPaymentMethodId: paymentMethodId, slug },
      });

      setBillingData((prevData) => ({
        ...prevData,
        paymentMethod: response.data.paymentMethod,
      }));
      setPaymentMethodId(response.data.paymentMethod.id);

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setActiveModal("");
    }
  };

  const cancelSubscription = async () => {
    try {
      setLoading("cancelSubscription");

      const response = await request({
        url: "/member/cancel-subscription",
        method: "POST",
        data: { subscriptionId: billingData.subscriptionId, slug },
      });

      setBillingData((prevData) => ({
        ...prevData,
        subscriptionStatus: response.data.status,
      }));

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
    }
  };

  const resumeSubscription = async () => {
    if (resumeData.paymentMethodId === null) {
      setError("Please select your payment method.");
      return;
    }

    try {
      setLoading("resumeSubscription");
      const response = await request({
        url: "/member/resume-subscription",
        method: "POST",
        data: {
          subscriptionPlan: resumeData.subscriptionPlan,
          paymentMethodId: resumeData.paymentMethodId,
          slug,
        },
      });

      setBillingData((prevData) => ({
        ...prevData,
        amount: response.data.amount,
        billingCycle: response.data.billingCycle,
        invoices: response.data.invoices,
        nextBillingDay: response.data.nextBillingDay,
        paymentMethod: response.data.paymentMethod,
        subscriptionId: response.data.subscriptionId,
        subscriptionStatus: response.data.subscriptionStatus,
      }));

      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setActiveModal("");
    }
  };

  const deleteMenu = async () => {
    try {
      setLoading("deleteMenu");

      const response = await request({
        url: "/member/delete-menu",
        method: "POST",
        data: { slug, password },
      });

      toast.success(response.data.message);

      navigate("/");
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setLoading("");
      setPassword("");
    }
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading("data");
        const response = await request({
          url: "/member/get-billing-data",
          method: "POST",
          data: { slug },
        });

        setBillingData(response.data.billingData);
        setPaymentMethodId(response.data.billingData.paymentMethod.id);
      } catch (error) {
        toast.error(error.response.data.message);
      } finally {
        setLoading("");
      }
    };

    getData();
  }, []);

  return (
    <Navigation isLoading={loading === "data"}>
      {activeModal === "changePaymentMethod" && (
        <Modal
          heading={"Change Payment Method"}
          paragraph={"Select payment method from your account."}
          closeModal={() => {
            setActiveModal("");
            setError("");
            setPaymentMethodId(billingData.paymentMethod.id);
          }}
          btnText={"Save"}
          isLoading={loading === "changePaymentMethod"}
          btnFuntion={
            loading === "changePaymentMethod" ? null : changePaymentMethod
          }
        >
          {billingData.allPaymentMethods?.map((paymentMethod) => {
            return (
              <div
                onClick={() => {
                  setPaymentMethodId(paymentMethod.id);
                }}
                className={`settings-payment-method-bar cursor-pointer mb-8 ${
                  paymentMethodId === paymentMethod.id &&
                  "settings-payment-method-bar-selected"
                }`}
                key={paymentMethod.id}
              >
                <div className="settings-payment-method-helper">
                  <img
                    className="settings-payment-method-img"
                    src={getCardBrandLogo(paymentMethod.brand)}
                  />
                  <div>
                    <p className="settings-payment-method-brand-p">
                      {paymentMethod?.brand?.toUpperCase()}
                    </p>
                    <p className="settings-payment-method-number-p">
                      Ending in {paymentMethod?.last4}
                    </p>
                  </div>
                </div>

                <div className="select-circle-div">
                  <div
                    className={`select-circle ${
                      paymentMethodId === paymentMethod.id &&
                      "select-circle-selected"
                    }`}
                  />
                </div>
              </div>
            );
          })}

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {activeModal === "resumeSubscription" && (
        <Modal
          heading={"Resume Subscription"}
          paragraph={"Select your subscription plan and payment method."}
          closeModal={() => {
            setActiveModal("");
            setError("");
            setResumeData({
              subscriptionPlan: "yearly",
              paymentMethodId: null,
            });
          }}
          btnText={"Resume"}
          isLoading={loading === "resumeSubscription"}
          btnFuntion={
            loading === "resumeSubscription" ? null : resumeSubscription
          }
        >
          <h3 className="modal-subheading">Subscription Plan</h3>
          <div className="menus-modal-subscriptions-div mb-16">
            <div
              onClick={() => {
                setResumeData((prevData) => ({
                  ...prevData,
                  subscriptionPlan: "yearly",
                }));
              }}
              className={`menus-modal-subscription-bar ${
                resumeData.subscriptionPlan === "yearly" &&
                "menus-modal-subscription-bar-selected"
              } `}
            >
              <div>
                <h4 className="menus-modal-subscription-h">Yearly Plan</h4>
                <p className="menus-modal-subscription-p">120$/year</p>
              </div>
            </div>

            <div
              onClick={() => {
                setResumeData((prevData) => ({
                  ...prevData,
                  subscriptionPlan: "monthly",
                }));
              }}
              className={`menus-modal-subscription-bar ${
                resumeData.subscriptionPlan === "monthly" &&
                "menus-modal-subscription-bar-selected"
              } `}
            >
              <div>
                <h4 className="menus-modal-subscription-h">Monthly Plan</h4>
                <p className="menus-modal-subscription-p">12$/month</p>
              </div>
            </div>
          </div>

          <h3 className="modal-subheading">Payment Method</h3>
          {billingData.allPaymentMethods?.map((paymentMethod) => {
            return (
              <div
                onClick={() => {
                  setResumeData((prevData) => ({
                    ...prevData,
                    paymentMethodId: paymentMethod.id,
                  }));
                }}
                className={`settings-payment-method-bar cursor-pointer mb-8 ${
                  resumeData.paymentMethodId === paymentMethod.id &&
                  "settings-payment-method-bar-selected"
                }`}
                key={paymentMethod.id}
              >
                <div className="settings-payment-method-helper">
                  <img
                    className="settings-payment-method-img"
                    src={getCardBrandLogo(paymentMethod.brand)}
                  />
                  <div>
                    <p className="settings-payment-method-brand-p">
                      {paymentMethod?.brand?.toUpperCase()}
                    </p>
                    <p className="settings-payment-method-number-p">
                      Ending in {paymentMethod?.last4}
                    </p>
                  </div>
                </div>

                <div className="select-circle-div">
                  <div
                    className={`select-circle ${
                      resumeData.paymentMethodId === paymentMethod.id &&
                      "select-circle-selected"
                    }`}
                  />
                </div>
              </div>
            );
          })}

          {error && <p className="login-form-error-p">{error}</p>}
        </Modal>
      )}

      {deleteModal.type === "cancelSubscription" && (
        <DeleteModal
          heading={"Cancel Subscription?"}
          paragraph={
            "This will disable your menu and your customers won't be able to view it."
          }
          deleteFunction={
            loading === "cancelSubscription" ? null : cancelSubscription
          }
          setDeleteModal={setDeleteModal}
          btnText="Cancel"
        ></DeleteModal>
      )}

      {deleteModal.type === "deleteMenu" && (
        <DeleteModal
          heading={"Delete Menu?"}
          paragraph={"This will delete all data for this menu."}
          deleteFunction={loading === "deleteMenu" ? null : deleteMenu}
          setDeleteModal={setDeleteModal}
          btnText="Delete"
        >
          <input
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            type="password"
            placeholder="Your password"
            className="classic-input"
          />
        </DeleteModal>
      )}

      <h1 className="page-content-h">Menu Billing</h1>
      <p className="page-content-p">
        Edit your payment method and view your menu invoices.
      </p>

      <div className="languages-container">
        <div className="billing-property-div">
          <div className="billing-property-heading-div">
            <h3 className="billing-property-heading">
              Subscription (
              {capitalizeFirstLetter(billingData?.subscriptionStatus)})
            </h3>

            {billingData.subscriptionStatus === "active" ||
            billingData.subscriptionStatus === "trialing" ? (
              <div
                onClick={() => {
                  setDeleteModal({ type: "cancelSubscription", id: "" });
                }}
                className="billing-red-btn"
              >
                Cancel
              </div>
            ) : (
              <div
                onClick={() => {
                  setActiveModal("resumeSubscription");
                }}
                className="billing-btn"
              >
                Resume
              </div>
            )}
          </div>
          {billingData.subscriptionStatus === "active" ||
            (billingData.subscriptionStatus === "trialing" && (
              <>
                <p className="billing-property-p">
                  ${billingData?.amount?.toFixed(2)}/{billingData.billingCycle}
                </p>
                <p className="billing-property-p">
                  Next payment on {formatDate(billingData?.nextBillingDay)}
                </p>
              </>
            ))}
        </div>

        {(billingData.subscriptionStatus === "active" ||
          billingData.subscriptionStatus === "trialing") && (
          <>
            <div className="billing-property-div">
              <div className="billing-property-heading-div">
                <h3 className="billing-property-heading">Payment Method</h3>

                <div
                  onClick={() => {
                    setActiveModal("changePaymentMethod");
                  }}
                  className="billing-btn"
                >
                  Change
                </div>
              </div>
              <div className="billing-payment-method-div">
                <img
                  className="settings-payment-method-img"
                  src={getCardBrandLogo(billingData?.paymentMethod?.brand)}
                />
                <div>
                  <p className="settings-payment-method-brand-p">
                    {capitalizeFirstLetter(billingData?.paymentMethod?.brand)}
                  </p>
                  <p className="settings-payment-method-number-p">
                    Ending in {billingData?.paymentMethod?.last4}
                  </p>
                </div>
              </div>
            </div>

            <div className="billing-property-div">
              <h3 className="billing-property-heading">Billing History</h3>

              <div className="billing-history-headings-div">
                <div className="billing-history-heading-div">DATE</div>
                <div className="billing-history-heading-div">STATUS</div>
                <div className="billing-history-heading-div">PRICE</div>
                <div className="billing-history-heading-div">INVOICE</div>
              </div>

              {billingData?.invoices?.map((invoice) => {
                return (
                  <div key={invoice.date} className="billing-invoice-div">
                    <p className="billing-invoice-p">
                      {formatDate(invoice.date)}
                    </p>
                    <p className="billing-invoice-p">
                      {capitalizeFirstLetter(invoice.status)}
                    </p>

                    <p className="billing-invoice-p">
                      ${invoice?.amount?.toFixed(2)}
                    </p>
                    <a
                      target="_blank"
                      href={invoice?.invoice_pdf}
                      className="billing-invoice-download-p"
                    >
                      View
                    </a>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="billing-property-div">
          <div className="billing-property-heading-div">
            <h3 className="billing-property-heading">Delete Menu</h3>

            <div
              onClick={() => {
                setDeleteModal({ type: "deleteMenu", id: "" });
              }}
              className="billing-red-btn"
            >
              Delete
            </div>
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default Billing;
